<template>
  <div class="app-container">
    <MapView 
      ref="mapViewRef" 
      :center="computedMapCenter" 
      :zoom="computedMapZoom" 
      :markerImageSrc="markerImageSrc"
      :companyMarkerImageSrc="companyMarkerImageSrc"
      :myPosition="computedMyPosition"
      :circleRadius="showCircle ? computedCircleRadius : 0"
      :showMyPosition="hasValidPosition"
      :inKqArea="computedInKqArea"
      :showCompanyMarker="hasValidCenter"
      :showAttendanceText="true"
      :showZoomControls="hasValidPosition"
    />
  </div>
</template>

<script>
import MapView from './MapView.vue'

export default {
  name: 'HomePage',
  components: {
    MapView
  },
  props: {
    // 路由参数传递的属性
    lng: Number,
    lat: Number,
    zoom: Number,
    circleRadius: Number,
    myLng: Number,
    myLat: Number,
    inKqArea: Boolean
  },
  data() {
    return {
      // 默认地图中心点坐标（企业位置）
      defaultMapCenter: [104.066541, 30.659462],
      // 默认我的位置
      defaultMyPosition: [104.066541 + 0.0006, 30.659462 + 0.0009],
      // 默认地图缩放级别
      defaultZoom: 14,
      // 默认考勤范围半径（米）
      defaultCircleRadius: 500,
      // 默认是否已进入考勤范围
      defaultInKqArea: true,
      // 我的位置标记图片路径
      markerImageSrc: '/image/kq_position_mine_blue.png',
      // 企业位置标记图片路径
      companyMarkerImageSrc: '/image/kq_position_company.png',
      // 本地计算的考勤状态
      calculatedInKqArea: null
    }
  },
  methods: {
    // 更新是否在考勤范围内（模拟计算）
    updateInKqAreaStatus() {
      // 只有当circleRadius > 0时才进行计算
      if (this.computedCircleRadius <= 0) {
        this.calculatedInKqArea = false;
        return;
      }
      
      // 这里简化为直线距离计算
      const distance = Math.sqrt(
        Math.pow(this.computedMyPosition[0] - this.computedMapCenter[0], 2) + 
        Math.pow(this.computedMyPosition[1] - this.computedMapCenter[1], 2)
      )
      
      // 简化转换：1度约等于111公里，根据半径进行判断
      const radiusInDegrees = this.computedCircleRadius / 111000
      this.calculatedInKqArea = distance <= radiusInDegrees
    }
  },
  
  // 监听路由参数变化
  watch: {
    // 监听路由参数变化
    $route: {
      handler() {
        // 更新考勤状态
        this.updateInKqAreaStatus()
      },
      immediate: true
    }
  },
  
  // 计算属性
  computed: {
    // 判断中心点坐标是否有效（不为null、undefined、0）
    hasValidCenter() {
      return this.lng !== null && this.lng !== undefined && this.lng !== 0 &&
             this.lat !== null && this.lat !== undefined && this.lat !== 0;
    },
    
    // 判断我的位置坐标是否有效（不为null、undefined、0）
    hasValidPosition() {
      return this.myLng !== null && this.myLng !== undefined && this.myLng !== 0 &&
             this.myLat !== null && this.myLat !== undefined && this.myLat !== 0;
    },
    
    // 是否显示圆形区域
    showCircle() {
      return this.hasValidCenter && this.computedCircleRadius > 0;
    },
    
    // 地图中心点（优先使用路由参数，最后是默认值）
    computedMapCenter() {
      if (this.lng !== null && this.lat !== null) {
        return [this.lng, this.lat]
      }
      return this.defaultMapCenter
    },
    
    // 我的位置（优先使用路由参数，最后是默认值）
    computedMyPosition() {
      if (this.myLng !== null && this.myLat !== null) {
        return [this.myLng, this.myLat]
      }
      return this.defaultMyPosition
    },
    
    // 地图缩放级别（优先使用路由参数，最后是默认值）
    computedMapZoom() {
      if (this.zoom !== null && !isNaN(this.zoom)) {
        return this.zoom
      }
      return this.defaultZoom
    },
    
    // 考勤范围半径（优先使用路由参数，最后是默认值）
    computedCircleRadius() {
      if (this.circleRadius !== null && !isNaN(this.circleRadius)) {
        return this.circleRadius
      }
      return this.defaultCircleRadius
    },
    
    // 是否已进入考勤范围（优先使用路由参数，最后是计算值）
    computedInKqArea() {
      if (this.inKqArea !== undefined) {
        return this.inKqArea
      }
      return this.calculatedInKqArea !== null ? this.calculatedInKqArea : this.defaultInKqArea
    }
  },
  
  mounted() {
    // 初始化考勤状态
    this.updateInKqAreaStatus()
  }
}
</script>

<style>
/* 重置默认样式 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  width: 100%;
  height: 100%;
  overflow: hidden;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app {
  width: 100%;
  height: 100%;
}

.app-container {
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  overflow: hidden;
  position: relative;
}

/* 控制面板样式 */
.control-panel {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 15px;
  max-width: 300px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: all 0.3s ease;
}

.control-panel h3 {
  margin-bottom: 15px;
  color: #333;
  font-size: 16px;
  text-align: center;
}

.control-group {
  margin-bottom: 15px;
}

.control-group label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: #666;
}

.control-group input[type="number"] {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.control-group input[type="range"] {
  width: calc(100% - 40px);
  vertical-align: middle;
}

.zoom-value {
  display: inline-block;
  width: 35px;
  text-align: center;
  font-size: 14px;
  color: #333;
}

.coordinate-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.coordinate-inputs input {
  flex: 1;
}

.button-group {
  margin-bottom: 15px;
}

.button-group button {
  width: 100%;
  padding: 10px;
  margin-bottom: 8px;
  background: #4285f4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}

.button-group button:hover {
  background: #3367d6;
}

.toggle-btn {
  background: #f8f9fa !important;
  color: #333 !important;
  border: 1px solid #ddd !important;
}

.toggle-btn:hover {
  background: #e9ecef !important;
}

.quick-locations {
  margin-top: 15px;
}

.quick-locations p {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.quick-locations button {
  padding: 6px 10px;
  margin: 2px;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.quick-locations button:hover {
  background: #e9ecef;
  border-color: #ccc;
}

/* 控制开关按钮 */
.control-toggle {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ccc;
  border-radius: 20px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 999;
  transition: all 0.3s ease;
}

.control-toggle:hover {
  background: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  html, body {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
  
  .control-panel {
    max-width: calc(100vw - 30px);
    font-size: 14px;
  }
  
  .control-group input[type="range"] {
    width: calc(100% - 35px);
  }
  
  .zoom-value {
    width: 30px;
    font-size: 12px;
  }
  
  .quick-locations button {
    font-size: 11px;
    padding: 5px 8px;
  }
}

/* 解决iOS Safari底部安全区域问题 */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .app-container {
    padding-bottom: env(safe-area-inset-bottom);
  }
  
  .control-toggle {
    bottom: calc(10px + env(safe-area-inset-bottom));
  }
}
</style>